import React, { useState } from 'react';
import { Box, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';

/**
 * categorical slots 1 and 2 of the validated palette.
 * checked with the dataviz palette validator against the MUI paper surfaces
 * ( light #ffffff , dark #121212 ) : lightness band , chroma floor , CVD separation ,
 * normal vision floor and contrast all PASS in both modes
 * ( worst adjacent CVD dE 24.7 light / 26.8 dark against an >= 8 target ).
 */
export const SERIES = {
  light: { previous: '#2a78d6', current: '#eb6834' },
  dark: { previous: '#3987e5', current: '#d95926' },
};

const GRID_STOPS = [100, 75, 50, 25, 0];
const PLOT_HEIGHT = 260;
//HEADROOM ABOVE THE PLOT SO THE 100% TICK AND THE DELTA LABELS ARE NEVER CLIPPED.
//overflow-x:auto FORCES overflow-y TO auto , SO ANYTHING SITTING ABOVE THE BOX GETS CUT.
const PLOT_HEADROOM = 18;
const Y_AXIS_WIDTH = 46;

const toPercent = (obtained, total) =>
  obtained === null || obtained === undefined || !total ? null : Math.max(0, Math.min(100, (obtained / total) * 100));

const flattenCriteria = (test) => {
  const out = [];
  (test?.criteria || []).forEach((parent) => {
    out.push({
      id: parent.student_examination_criteria_id,
      label: parent.criteria_name,
      level: 0,
      obtained: parent.marks_obtained,
      total: parent.total_marks,
    });
    (parent.subCriteria || []).forEach((sub) => {
      out.push({
        id: sub.student_examination_criteria_id,
        label: sub.criteria_name,
        level: 1,
        obtained: sub.marks_obtained,
        total: sub.total_marks,
      });
    });
  });
  return out;
};

/**
 * union of the two tests' criteria , current test order first
 * @author Akhil E M
 * @createdAt 05-08-2026
 * @updates 05-08-2026(Akhil EM),
 * @type function
 * @param {object} currentTest
 * @param {object} previousTest
 * @returns {array}
 */
export const buildChartRows = (currentTest, previousTest) => {
  const current = flattenCriteria(currentTest);
  const previous = flattenCriteria(previousTest);
  const previousById = new Map(previous.map((r) => [r.id, r]));
  const seen = new Set();

  const rows = current.map((row) => {
    seen.add(row.id);
    const prior = previousById.get(row.id);
    return {
      id: row.id,
      label: row.label,
      level: row.level,
      current: { obtained: row.obtained, total: row.total, pct: toPercent(row.obtained, row.total) },
      previous: prior
        ? { obtained: prior.obtained, total: prior.total, pct: toPercent(prior.obtained, prior.total) }
        : null,
    };
  });

  //CRITERIA SCORED LAST TIME BUT NOT THIS TIME MUST STILL BE VISIBLE
  previous.forEach((row) => {
    if (seen.has(row.id)) return;
    rows.push({
      id: row.id,
      label: row.label,
      level: row.level,
      current: null,
      previous: { obtained: row.obtained, total: row.total, pct: toPercent(row.obtained, row.total) },
    });
  });

  return rows;
};

const Column = ({ pct, color, dim }) => {
  //A CRITERION THAT WAS NOT SCORED GETS AN EXPLICIT EMPTY SLOT , OTHERWISE A MISSING
  //COLUMN READS AS A ZERO SCORE RATHER THAN AS ABSENT DATA
  if (pct === null) {
    return (
      <Box
        sx={{
          width: { xs: 14, sm: 18 },
          height: 6,
          borderRadius: '2px',
          border: '1px dashed',
          borderColor: 'divider',
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        width: { xs: 14, sm: 18 },
        height: `${pct}%`,
        minHeight: pct ? 3 : 0,
        bgcolor: color,
        opacity: dim ? 0.35 : 1,
        //4px ROUNDED CAP , SQUARE AT THE BASELINE IT GROWS FROM
        borderRadius: '4px 4px 0 0',
        transition: 'height 350ms ease, opacity 150ms ease',
      }}
    />
  );
};

const SummaryStat = ({ label, value, tone }) => {
  const theme = useTheme();
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        noWrap
        sx={{
          fontWeight: 600,
          color: tone === 'up' ? theme.palette.success.main : tone === 'down' ? theme.palette.error.main : 'text.primary',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
};

/**
 * grouped column chart of criteria scores.
 * one series when there is no previous test , two side by side columns when comparing.
 * @author Akhil E M
 * @createdAt 05-08-2026
 * @updates 05-08-2026(Akhil EM),
 * @type component
 * @packages @mui/material,@iconify/react
 */
const ExamScoreChart = ({ currentTest, previousTest, currentLabel, previousLabel }) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(null);
  const palette = theme.palette.mode === 'dark' ? SERIES.dark : SERIES.light;
  const isComparison = Boolean(previousTest?.hasBreakdown);

  const rows = buildChartRows(currentTest, isComparison ? previousTest : null);
  if (!rows.length) return null;

  const gridLine = alpha(theme.palette.text.primary, 0.08);
  const axisLine = alpha(theme.palette.text.primary, 0.18);

  /* ---------------- SUMMARY ---------------- */
  const scored = rows.filter((r) => r.current?.pct !== null && r.current);
  const average = scored.length ? scored.reduce((a, r) => a + r.current.pct, 0) / scored.length : null;
  const comparable = rows.filter((r) => r.current?.pct != null && r.previous?.pct != null);
  const avgDelta = comparable.length
    ? comparable.reduce((a, r) => a + (r.current.pct - r.previous.pct), 0) / comparable.length
    : null;
  const best = scored.length ? scored.reduce((a, r) => (r.current.pct > a.current.pct ? r : a)) : null;
  const weakest = scored.length ? scored.reduce((a, r) => (r.current.pct < a.current.pct ? r : a)) : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(
          theme.palette.background.paper,
          0,
        )} 40%)`,
      }}
    >
      {/* ---------------- SUMMARY STRIP ---------------- */}
      <Stack
        direction="row"
        divider={<Box sx={{ width: '1px', bgcolor: 'divider', alignSelf: 'stretch' }} />}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <SummaryStat label="Average score" value={average === null ? '—' : `${average.toFixed(0)}%`} />
        {isComparison && (
          <SummaryStat
            label="Average change"
            value={avgDelta === null ? '—' : `${avgDelta >= 0 ? '+' : ''}${avgDelta.toFixed(0)}%`}
            tone={avgDelta === null ? undefined : avgDelta >= 0 ? 'up' : 'down'}
          />
        )}
        <SummaryStat label="Strongest" value={best ? `${best.current.pct.toFixed(0)}%` : '—'} />
        <SummaryStat label="Weakest" value={weakest ? `${weakest.current.pct.toFixed(0)}%` : '—'} />
      </Stack>

      {/* ---------------- LEGEND ( ALWAYS PRESENT FOR TWO SERIES ) ---------------- */}
      {isComparison && (
        <Stack direction="row" spacing={2.5} sx={{ mb: 2.5 }}>
          {[
            { label: previousLabel, color: palette.previous },
            { label: currentLabel, color: palette.current },
          ].map((s) => (
            <Stack key={s.label} direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: s.color, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {s.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {/* ---------------- PLOT ---------------- */}
      {/* THE Y AXIS SITS OUTSIDE THE SCROLLER SO IT STAYS PUT WHILE THE COLUMNS SCROLL */}
      <Box sx={{ display: 'flex' }}>
        <Box
          sx={{
            width: Y_AXIS_WIDTH,
            flexShrink: 0,
            position: 'relative',
            height: PLOT_HEIGHT,
            mt: `${PLOT_HEADROOM}px`,
          }}
        >
          {GRID_STOPS.map((stop) => (
            <Typography
              key={stop}
              variant="caption"
              sx={{
                position: 'absolute',
                right: 8,
                bottom: `${stop}%`,
                //CENTRES EACH TICK ON ITS GRIDLINE INSTEAD OF NUDGING THE WHOLE COLUMN UP
                transform: 'translateY(50%)',
                color: 'text.disabled',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1,
              }}
            >
              {stop}%
            </Typography>
          ))}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowX: 'auto',
            pb: 0.5,
            //SAME SLIM SCROLLBAR AS THE TWO PANES
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(theme.palette.text.primary, 0.18)} transparent`,
            '&::-webkit-scrollbar': { width: 6, height: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.18),
              borderRadius: 999,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: alpha(theme.palette.text.primary, 0.32),
            },
          }}
        >
          <Box sx={{ minWidth: rows.length * 74 }}>
            <Box sx={{ position: 'relative', height: PLOT_HEIGHT, mt: `${PLOT_HEADROOM}px` }}>
            {/* HAIRLINE GRID , SOLID AND RECESSIVE */}
            {GRID_STOPS.map((stop) => (
              <Box
                key={stop}
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: `${stop}%`,
                  height: '1px',
                  bgcolor: stop === 0 ? axisLine : gridLine,
                }}
              />
            ))}

            {/* COLUMNS */}
            <Stack direction="row" alignItems="flex-end" sx={{ height: '100%', position: 'relative' }}>
              {rows.map((row) => {
                const delta =
                  row.current?.pct != null && row.previous?.pct != null ? row.current.pct - row.previous.pct : null;
                const active = hovered === `${row.id}-${row.level}`;

                return (
                  <Tooltip
                    key={`${row.id}-${row.level}`}
                    placement="top"
                    title={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                          {row.label}
                        </Typography>
                        {isComparison && (
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            {previousLabel}: {row.previous ? `${row.previous.obtained}/${row.previous.total}` : '—'}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {currentLabel}: {row.current ? `${row.current.obtained}/${row.current.total}` : 'not scored'}
                        </Typography>
                        {delta !== null && (
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                            Change: {delta >= 0 ? '+' : ''}
                            {delta.toFixed(0)}%
                          </Typography>
                        )}
                      </Box>
                    }
                  >
                    <Box
                      onMouseEnter={() => setHovered(`${row.id}-${row.level}`)}
                      onMouseLeave={() => setHovered(null)}
                      sx={{
                        flex: 1,
                        height: '100%',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        //THE HIT AREA IS THE WHOLE COLUMN SLOT , NOT JUST THE MARK
                        gap: '2px',
                        position: 'relative',
                        cursor: 'default',
                        borderRadius: 1,
                        bgcolor: active ? alpha(theme.palette.text.primary, 0.04) : 'transparent',
                      }}
                    >
                      {/* SELECTIVE DIRECT LABEL : THE CHANGE IS THE STORY , NOT EVERY VALUE .
                          ANCHORED ABOVE THE TALLER COLUMN SO IT STAYS ATTACHED TO ITS MARK
                          INSTEAD OF FLOATING AT THE TOP OF THE PLOT */}
                      {delta !== null && (
                        <Stack
                          direction="row"
                          spacing={0.15}
                          alignItems="center"
                          sx={{
                            position: 'absolute',
                            bottom: `calc(${Math.max(row.current?.pct ?? 0, row.previous?.pct ?? 0)}% + 6px)`,
                            left: '50%',
                            transform: 'translateX(-50%)',
                          }}
                        >
                          <Icon
                            icon={delta >= 0 ? 'mdi:arrow-up' : 'mdi:arrow-down'}
                            width="12"
                            height="12"
                            color={delta >= 0 ? theme.palette.success.main : theme.palette.error.main}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              fontSize: 11,
                              color: delta >= 0 ? 'success.main' : 'error.main',
                            }}
                          >
                            {delta >= 0 ? '+' : ''}
                            {delta.toFixed(0)}
                          </Typography>
                        </Stack>
                      )}

                      {/* SINGLE SERIES SHOWS THE VALUE ON THE CAP */}
                      {!isComparison && row.current?.pct != null && (
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            bottom: `calc(${row.current.pct}% + 4px)`,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            color: 'text.secondary',
                          }}
                        >
                          {row.current.obtained}
                        </Typography>
                      )}

                      {isComparison && (
                        <Column pct={row.previous?.pct ?? null} color={palette.previous} dim={hovered && !active} />
                      )}
                      <Column pct={row.current?.pct ?? null} color={palette.current} dim={hovered && !active} />
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>

            {/* X AXIS , IN NORMAL FLOW SO THE LABEL BAND IS NEVER CLIPPED */}
            <Stack direction="row" sx={{ mt: 1.25 }}>
              {rows.map((row) => (
                <Tooltip key={`${row.id}-${row.level}-label`} title={row.label || ''} placement="bottom">
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1,
                      px: 0.5,
                      textAlign: 'center',
                      lineHeight: 1.25,
                      color: row.level ? 'text.disabled' : 'text.secondary',
                      fontWeight: row.level ? 400 : 600,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {row.label || '—'}
                  </Typography>
                </Tooltip>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default ExamScoreChart;
